import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../context/CompanyContext';
import api from '../../services/api';
import './BankReconciliation.css';

const BankReconciliation = () => {
  const { companyId } = useCompany();
  const queryClient = useQueryClient();
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [formData, setFormData] = useState({
    bankName: 'Bancolombia',
    accountNumber: '',
    accountType: 'corriente',
    clientId: '',
    clientSecret: ''
  });

  // Obtener cuentas bancarias
  const { data: bankAccounts, isLoading, error } = useQuery({
    queryKey: ['bankAccounts', companyId],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/bank-accounts`);
      return response.data.data.bankAccounts || [];
    },
    enabled: !!companyId
  });

  // Obtener transacciones de cuenta seleccionada
  const { data: transactions } = useQuery({
    queryKey: ['bankTransactions', companyId, selectedAccount?.id],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/bank-accounts/${selectedAccount.id}/transactions`);
      return response.data.data.transactions || [];
    },
    enabled: !!companyId && !!selectedAccount
  });

  // Crear cuenta bancaria
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post(`/companies/${companyId}/bank-accounts`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['bankAccounts', companyId]);
      setShowConnectForm(false);
      setFormData({
        bankName: 'Bancolombia',
        accountNumber: '',
        accountType: 'corriente',
        clientId: '',
        clientSecret: ''
      });
    }
  });

  // Sincronizar transacciones
  const syncMutation = useMutation({
    mutationFn: async (accountId) => {
      const response = await api.post(`/companies/${companyId}/bank-accounts/${accountId}/sync`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['bankTransactions', companyId, selectedAccount?.id]);
      queryClient.invalidateQueries(['bankAccounts', companyId]);
      alert('Transacciones sincronizadas exitosamente');
    }
  });

  // Conciliar transacción
  const reconcileMutation = useMutation({
    mutationFn: async ({ transactionId, entryLineId }) => {
      const response = await api.post(
        `/companies/${companyId}/bank-accounts/${selectedAccount.id}/transactions/${transactionId}/reconcile`,
        { entryLineId }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['bankTransactions', companyId, selectedAccount?.id]);
      alert('Transacción conciliada exitosamente');
    }
  });

  const handleConnect = (e) => {
    e.preventDefault();
    createMutation.mutate({
      bankName: formData.bankName,
      accountNumber: formData.accountNumber,
      accountType: formData.accountType,
      credentials: {
        clientId: formData.clientId,
        clientSecret: formData.clientSecret
      }
    });
  };

  if (isLoading) return <div>Cargando cuentas bancarias...</div>;
  if (error) return <div>Error al cargar cuentas bancarias</div>;

  return (
    <div className="bank-reconciliation">
      <div className="page-header">
        <h2>Conciliación Bancaria</h2>
        <button 
          className="btn-primary"
          onClick={() => setShowConnectForm(!showConnectForm)}
        >
          {showConnectForm ? 'Cancelar' : '+ Conectar Cuenta'}
        </button>
      </div>

      {showConnectForm && (
        <div className="form-card">
          <h3>Conectar Cuenta Bancaria</h3>
          <form onSubmit={handleConnect}>
            <div className="form-row">
              <div className="form-group">
                <label>Banco *</label>
                <select
                  value={formData.bankName}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                  required
                >
                  <option value="Bancolombia">Bancolombia</option>
                  <option value="Davivienda">Davivienda</option>
                  <option value="Banco de Bogotá">Banco de Bogotá</option>
                  <option value="Banco Popular">Banco Popular</option>
                </select>
              </div>
              <div className="form-group">
                <label>Tipo de Cuenta *</label>
                <select
                  value={formData.accountType}
                  onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                  required
                >
                  <option value="corriente">Corriente</option>
                  <option value="ahorros">Ahorros</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Número de Cuenta *</label>
              <input
                type="text"
                value={formData.accountNumber}
                onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Client ID *</label>
                <input
                  type="text"
                  value={formData.clientId}
                  onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Client Secret *</label>
                <input
                  type="password"
                  value={formData.clientSecret}
                  onChange={(e) => setFormData({ ...formData, clientSecret: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={createMutation.isLoading}>
                {createMutation.isLoading ? 'Conectando...' : 'Conectar'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bank-accounts-section">
        <h3>Cuentas Bancarias</h3>
        {bankAccounts && bankAccounts.length > 0 ? (
          <div className="bank-accounts-grid">
            {bankAccounts.map(account => (
              <div
                key={account.id}
                className={`bank-account-card ${selectedAccount?.id === account.id ? 'selected' : ''}`}
                onClick={() => setSelectedAccount(account)}
              >
                <div className="bank-icon">🏦</div>
                <div className="account-info">
                  <h4>{account.bankName}</h4>
                  <p>{account.accountNumber}</p>
                  <p className="account-type">{account.accountType}</p>
                  <div className="account-status">
                    {account.isConnected ? (
                      <span className="status-connected">✓ Conectada</span>
                    ) : (
                      <span className="status-disconnected">✗ Desconectada</span>
                    )}
                  </div>
                  {account.lastSync && (
                    <p className="last-sync">
                      Última sync: {new Date(account.lastSync).toLocaleDateString()}
                    </p>
                  )}
                </div>
                {account.isConnected && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      syncMutation.mutate(account.id);
                    }}
                    className="btn-sync"
                    disabled={syncMutation.isLoading}
                  >
                    {syncMutation.isLoading ? 'Sincronizando...' : 'Sincronizar'}
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No hay cuentas bancarias conectadas</p>
          </div>
        )}
      </div>

      {selectedAccount && (
        <div className="transactions-section">
          <h3>Transacciones - {selectedAccount.bankName}</h3>
          {transactions && transactions.length > 0 ? (
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Descripción</th>
                  <th>Tipo</th>
                  <th>Monto</th>
                  <th>Saldo</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(txn => (
                  <tr key={txn.id} className={txn.isReconciled ? 'reconciled' : ''}>
                    <td>{new Date(txn.transactionDate).toLocaleDateString()}</td>
                    <td>{txn.description}</td>
                    <td>
                      <span className={`txn-type txn-${txn.transactionType}`}>
                        {txn.transactionType}
                      </span>
                    </td>
                    <td className="amount">
                      {txn.transactionType === 'debito' ? '-' : '+'}
                      ${txn.amount.toFixed(2)}
                    </td>
                    <td>${txn.balance.toFixed(2)}</td>
                    <td>
                      {txn.isReconciled ? (
                        <span className="status-reconciled">✓ Conciliada</span>
                      ) : (
                        <span className="status-pending">Pendiente</span>
                      )}
                    </td>
                    <td>
                      {!txn.isReconciled && (
                        <button
                          onClick={() => reconcileMutation.mutate({ transactionId: txn.id })}
                          className="btn-link"
                          disabled={reconcileMutation.isLoading}
                        >
                          Conciliar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <p>No hay transacciones. Sincroniza la cuenta para obtener transacciones.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BankReconciliation;

